/**
 * The branch read (1.2, 2.2, 2.4, 5.1): **one number per cell** by week (or day)
 * × discipline group × territory, in points.
 *
 * Rebuilt 2026-09-21 because Pete could not read it: *"the UI is pretty
 * overwhelming and I'm lost with respect to what's going on in here."* What was
 * there carried six encodings per cell over up to thirty rows, and the bars were
 * scaled **per cell**, so a full bar here and a full bar there meant different
 * quantities. There are no bars now.
 *
 * What a reader gets instead:
 *
 *   - **Six rows**, one per discipline group. Territories expand on click.
 *   - **One big number** and one detail line per cell. Two flavours behind a
 *     toggle, and **the panel title names the selected one** — there is no
 *     legend, so that heading is the only thing telling a reader what the
 *     numbers are (the script's 5.1 Show line quotes them; they supersede the
 *     old **Against target** title). The heading and the toggle were briefly the same control; they are
 *     not any more (Pete, 2026-09-21).
 *   - **A continuous ramp**: white at nothing scheduled, `#B6D4BB` at target,
 *     one red past it, grey where there is no target at all.
 *   - **The worst territory in red** on a collapsed group row. Without it the
 *     rollup hides the cell Act 5.1 is about: SN reads 36 points open for three
 *     weeks running while North is 7.5 to 9 over.
 *
 * **Two layers, not three.** Planned is `plotted`; everything the branch has
 * promised and not yet delivered is **Reserved**, `provisional + estimated`, and
 * the hover names the reasons inside it — held for SOC, pending auth, awaiting
 * plan. The arithmetic underneath is untouched, so `committedPoints` and the
 * referral panel's totals cannot move because a read did.
 *
 * Supply lands by owned territory, demand by patient territory, so a territory
 * past its target reads red: the engine is already crossing lines to serve it.
 * By day is where a hold moving off a Thursday puts that Thursday back into the
 * read.
 */
import { useMemo, useState } from "react";
import { Box, Button, ButtonGroup, MenuItem, Paper, Select, Stack, Tooltip, Typography } from "@mui/material";
import { GROUP_TOTAL_TERRITORY, selectCellClinicians, selectPointsCells, type Granularity, type PointsCell } from "../../engine/capacity";
import { describeReserved, reservedTotal } from "../../engine/reserved";
import { addDaysToKey, dateKeyRange, dateKeyStart, formatInBranchZone, isoWeekday, weekStartKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { disciplineGroupFor } from "../../engine/roster";
import { territoryNames } from "@caredna-marlin/utils/homeHealthDemo/engine/territory";
import type { DemoState, DisciplineGroup } from "@caredna-marlin/utils/homeHealthDemo/state";
import { useDemoState } from "../../store/useDemoStore";

const GROUP_ORDER: DisciplineGroup[] = ["SN", "PT", "OT", "SLP", "MSW", "HHA"];

/**
 * The two flavours, and **the titles**. `Percent of target` rather than percent
 * utilization on purpose: the rule underneath the script's vocabulary is that
 * utilization is never applied to a person, and this panel hovers individuals.
 */
export type HeatMapFlavour = "remaining" | "percent";

export const FLAVOUR_TITLE: Record<HeatMapFlavour, string> = {
  remaining: "Remaining capacity",
  percent: "Percent of target",
};

/**
 * What the toggle says, as against what the heading says. The heading carries
 * the full name a beat away from the buttons, so the control only has to name
 * the side you are not on; two long strings in a segmented control crowd the row
 * the granularity and territory controls share with it.
 */
export const FLAVOUR_BUTTON: Record<HeatMapFlavour, string> = {
  remaining: "Remaining",
  percent: "Percent",
};

/** Red text, the same one the over-target marker and the negative numbers use. */
const RED = "#a63d3d";

export interface HeatMapProps {
  weekStart?: string;
  /**
   * Separate spans on purpose. **By day** is a fortnight, so 1.3's Thursday comes
   * back as a column wide enough to point at instead of one of twenty-eight
   * single letters.
   *
   * **By week** stops at four, and the reason it used to give is no longer true:
   * the horizon was twenty-eight days, so a fifth column read as a cliff rather
   * than as a quiet week. The seed and the read now reach thirteen weeks, so a
   * longer span would carry real shifts. Four is kept because it is what the
   * script's Act 5 beat points at, not because it is forced — widen it if the
   * beat wants more.
   */
  weekSpanWeeks?: 2 | 4;
  daySpanWeeks?: 2 | 4;
}

function round4(n: number): number {
  return Math.round(n * 4) / 4;
}

function points(n: number): string {
  return `${round4(n)}`;
}

/** A negative points figure reads with a real minus sign, never a hyphen. */
function signedPoints(n: number): string {
  return n < 0 ? `−${points(-n)}` : points(n);
}

/**
 * Everything a cell draws, off the three layers the engine still carries.
 * `committed` is Planned plus Reserved and is the only thing the colour reads.
 */
export interface CellRead {
  planned: number;
  reserved: number;
  committed: number;
  target: number;
  /** Points open: negative when the cell is past its target. */
  remaining: number;
  /** `undefined` where there is no target to be a percent of. */
  pct: number | undefined;
}

export function readCell(cell: PointsCell): CellRead {
  const planned = round4(cell.plotted);
  const reserved = reservedTotal(cell.reserved);
  const committed = round4(planned + reserved);
  const target = round4(cell.target);
  return { planned, reserved, committed, target, remaining: round4(target - committed), pct: target > 0 ? Math.round((committed / target) * 100) : undefined };
}

export type RampKind = "none" | "under" | "over";

/** `#B6D4BB`, the colour a cell reaches exactly at its target. */
const RAMP_AT_TARGET = [182, 212, 187] as const;
const RAMP_OVER = "#F0C9C9";
/** No target at all. **No target outranks no work**: "nobody owns this" is a different fact from "nobody is working here". */
const RAMP_NO_TARGET = "#F2F4F6";

export function rampFor(committed: number, target: number): { color: string; kind: RampKind } {
  if (target <= 0) return { color: RAMP_NO_TARGET, kind: "none" };
  if (committed > target) return { color: RAMP_OVER, kind: "over" };
  const t = Math.max(0, Math.min(1, committed / target));
  const channel = (to: number) => Math.round(255 + (to - 255) * t);
  return { color: `rgb(${channel(RAMP_AT_TARGET[0])}, ${channel(RAMP_AT_TARGET[1])}, ${channel(RAMP_AT_TARGET[2])})`, kind: "under" };
}

export interface WorstTerritory {
  territory: string;
  remaining: number;
  pct: number;
}

/**
 * The territory furthest over **its own** target, which is why the rank is the
 * ratio and not the deficit: a small territory eight points over is in worse
 * trouble than a large one eight points over. One rule for both flavours, so
 * flipping the toggle changes the marker's unit and never which territory it
 * names. Territories with no target are not candidates — grey means nobody owns
 * the work, not that somebody is drowning in it.
 */
export function worstTerritory(cells: (PointsCell | undefined)[]): WorstTerritory | undefined {
  let best: (WorstTerritory & { ratio: number }) | undefined;
  for (const cell of cells) {
    if (!cell || cell.target <= 0) continue;
    const read = readCell(cell);
    if (read.committed <= read.target) continue;
    const ratio = read.committed / read.target;
    if (!best || ratio > best.ratio) best = { territory: cell.territory, ratio, remaining: read.remaining, pct: read.pct ?? 0 };
  }
  return best;
}

export function HeatMap({ weekStart, weekSpanWeeks = 4, daySpanWeeks = 2 }: HeatMapProps) {
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const [granularity, setGranularity] = useState<Granularity>("week");
  const [flavour, setFlavour] = useState<HeatMapFlavour>("remaining");
  const [openGroups, setOpenGroups] = useState<DisciplineGroup[]>([]);
  const [territory, setTerritory] = useState<string>("All");
  const start = weekStart ?? weekStartKey(state.meta.demoToday);
  const spanWeeks = granularity === "day" ? daySpanWeeks : weekSpanWeeks;
  const rangeEnd = addDaysToKey(start, spanWeeks * 7 - 1);
  const territories = territoryNames(state.config.branch);
  const shown = territory === "All" ? territories : [territory];
  const groups = useMemo(() => GROUP_ORDER.filter((g) => Object.values(state.roster).some((e) => disciplineGroupFor(e.discipline) === g)), [state.roster]);

  const cells = useMemo(
    () => selectPointsCells(state, { rangeStart: start, rangeEnd, granularity, groups, territories: shown, groupTotals: true }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, start, rangeEnd, granularity, groups.join(","), shown.join(",")],
  );
  const buckets = granularity === "day" ? dateKeyRange(start, rangeEnd) : dateKeyRange(start, rangeEnd).filter((d) => isoWeekday(d) === 1);
  const rows = useMemo(() => buildRows(cells, groups, shown), [cells, groups, shown]);
  const toggleGroup = (group: DisciplineGroup) => setOpenGroups((open) => (open.includes(group) ? open.filter((g) => g !== group) : [...open, group]));
  const visible = rows.filter((r) => r.total || openGroups.includes(r.group));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {/* One row, styled like the one on Who is working: a `subtitle1` heading,
          then `ButtonGroup`s of contained/outlined buttons and the same
          `size="small"` selects — Pete, 2026-09-21: *"match the style of the
          buttons on the who is working page and put them inline with the
          title"*. Before that the two flavour names **were** the title, the
          selected one at 16px bold beside the other at 12px grey, which read as
          two mismatched headings with no control on screen. */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {FLAVOUR_TITLE[flavour]}
        </Typography>
        {/* Just the unit, and only where there is one. The caption used to add
            *placeholder values until the branch's table lands*, which read on
            screen as a loading error — the word "table" collides with the table
            you are looking at — and said unprompted what `demo-script.md`
            (Notes, Point values) says to say **only if asked**. The disclosure still matters:
            about 0.9% of the points on this screen come from a value Colin has
            confirmed, the rest from ours. It belongs in the talk track, where the
            script already has it. */}
        {flavour === "remaining" && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: -1 }}>
            points
          </Typography>
        )}
        {/* Short labels on the buttons. The heading beside them already carries
            the full name, and two long strings would crowd the row. */}
        <ButtonGroup size="small">
          {(["remaining", "percent"] as HeatMapFlavour[]).map((f) => (
            <Button key={f} variant={flavour === f ? "contained" : "outlined"} aria-pressed={flavour === f} onClick={() => setFlavour(f)}>
              {FLAVOUR_BUTTON[f]}
            </Button>
          ))}
        </ButtonGroup>
        <ButtonGroup size="small">
          {(["week", "day"] as Granularity[]).map((g) => (
            <Button key={g} variant={granularity === g ? "contained" : "outlined"} aria-pressed={granularity === g} onClick={() => setGranularity(g)}>
              {g === "week" ? "By week" : "By day"}
            </Button>
          ))}
        </ButtonGroup>
        <Select size="small" value={territory} onChange={(e) => setTerritory(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="All">Territory: All</MenuItem>
          {territories.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <Box sx={{ overflowX: "auto" }}>
        <Box component="table" sx={{ borderCollapse: "collapse", tableLayout: "fixed", minWidth: granularity === "day" ? 1100 : 900, width: "100%", "& th, & td": { border: "1px solid #e0e0e0", p: 0 } }}>
          <thead>
            <tr>
              <Box component="th" sx={{ width: 190, textAlign: "left", px: 1, py: 0.5, bgcolor: "#f3f3f3", fontSize: 11.5 }}>
                Discipline · territory
              </Box>
              {buckets.map((b) => (
                <Box component="th" key={b} sx={{ py: 0.5, bgcolor: b === state.meta.demoToday ? "#fff4d6" : "#f3f3f3", fontSize: 10.5, fontWeight: 600, color: isoWeekday(b) >= 6 && granularity === "day" ? "text.secondary" : undefined }}>
                  {granularity === "day" ? formatInBranchZone(dateKeyStart(b, tz), tz, spanWeeks === 2 ? "EEE d" : "EEEEE d") : `wk ${formatInBranchZone(dateKeyStart(b, tz), tz, "MMM d")}`}
                </Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => {
              const open = openGroups.includes(r.group);
              return (
                <tr key={r.key} data-row={r.key}>
                  <Box component="td" sx={{ px: r.total ? 0 : 1, py: r.total ? 0 : 0.25, fontSize: r.total ? 11.5 : 11, fontWeight: r.total ? 600 : 400, color: r.total ? undefined : "text.secondary", bgcolor: r.total ? "#fafafa" : undefined, whiteSpace: "nowrap" }}>
                    {r.total ? (
                      <Box
                        component="button"
                        type="button"
                        aria-expanded={open}
                        onClick={() => toggleGroup(r.group)}
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%", px: 1, py: 0.5, border: "none", bgcolor: "transparent", font: "inherit", fontWeight: 600, textAlign: "left", cursor: "pointer" }}
                      >
                        <Box component="span" aria-hidden sx={{ fontSize: 9, color: "text.secondary", transform: open ? "rotate(90deg)" : undefined, transition: "transform 120ms" }}>
                          ▶
                        </Box>
                        <Box component="span">{r.label}</Box>
                      </Box>
                    ) : (
                      <Box component="span" sx={{ pl: 2 }}>
                        {r.label}
                      </Box>
                    )}
                  </Box>
                  {buckets.map((b) => (
                    <HeatCell key={b} cell={r.cells[b]} flavour={flavour} marker={r.total && !open ? worstTerritory(r.children?.[b] ?? []) : undefined} state={state} group={r.group} territories={r.territories} bucket={b} />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </Box>
      </Box>
    </Paper>
  );
}

interface Row {
  key: string;
  label: string;
  total: boolean;
  group: DisciplineGroup;
  territories: string[];
  cells: Record<string, PointsCell | undefined>;
  /** Group rows only: the territory cells underneath, per bucket, for the collapsed row's marker. */
  children?: Record<string, PointsCell[]>;
}

export function buildRows(cells: PointsCell[], groups: DisciplineGroup[], territories: string[]): Row[] {
  const rows: Row[] = [];
  const buckets = Array.from(new Set(cells.map((c) => c.bucket)));
  for (const group of groups) {
    const mine = cells.filter((c) => c.group === group);
    const children = mine.filter((c) => c.territory !== GROUP_TOTAL_TERRITORY);
    if (!children.length) continue;
    const totals = mine.filter((c) => c.territory === GROUP_TOTAL_TERRITORY);
    rows.push({
      key: group,
      label: group,
      total: true,
      group,
      territories,
      cells: Object.fromEntries(buckets.map((b) => [b, totals.find((c) => c.bucket === b)])),
      children: Object.fromEntries(buckets.map((b) => [b, children.filter((c) => c.bucket === b)])),
    });
    for (const t of territories) {
      const tc = mine.filter((c) => c.territory === t);
      if (!tc.length) continue;
      rows.push({ key: `${group}|${t}`, label: t, total: false, group, territories: [t], cells: Object.fromEntries(tc.map((c) => [c.bucket, c])) });
    }
  }
  return rows;
}

function HeatCell({ cell, flavour, marker, state, group, territories, bucket }: { cell?: PointsCell; flavour: HeatMapFlavour; marker?: WorstTerritory; state: DemoState; group: DisciplineGroup; territories: string[]; bucket: string }) {
  if (!cell) return <Box component="td" sx={{ height: 42 }} />;
  const read = readCell(cell);
  const ramp = rampFor(read.committed, read.target);
  // **A cell with no target cannot be over one.** `target − committed` is a
  // negative there and rendering it signed and red says "over" about a
  // discipline nobody owns in this territory — the opposite of what the grey
  // ramp is saying underneath it. So Remaining shows the work as a plain
  // positive, and Percent has no denominator and shows nothing.
  const noTarget = read.target <= 0;
  const big =
    flavour === "remaining"
      ? noTarget
        ? read.committed === 0
          ? "—"
          : `+${points(read.committed)}`
        : signedPoints(read.remaining)
      : read.pct === undefined
        ? "—"
        : `${read.pct}%`;
  // Identical in both flavours: the layers and the denominator, in words the
  // panel no longer has room to spell out. The hover spells them out.
  const detail = read.reserved ? `${points(read.planned)} + ${points(read.reserved)} of ${points(read.target)}` : `${points(read.planned)} of ${points(read.target)}`;
  const markerText = marker ? (flavour === "remaining" ? `${marker.territory} ${signedPoints(marker.remaining)}` : `${marker.territory} ${marker.pct}%`) : undefined;
  const layers = `Planned ${points(read.planned)} · Reserved ${points(read.reserved)} · target ${points(read.target)}`;
  const breakdown = `reserved: ${describeReserved(cell.reserved)}`;
  // The per-clinician lines are built when the tooltip opens, not on every render of every cell.
  return (
    <Tooltip title={<CellTip layers={layers} breakdown={breakdown} state={state} group={group} territories={territories} weekStart={bucket} tz={state.config.branch.timeZone} />}>
      <Box component="td" data-ramp={ramp.kind} style={{ backgroundColor: ramp.color }} sx={{ height: 42, px: 0.5, textAlign: "center", verticalAlign: "middle" }}>
        <Typography component="div" sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.1, color: flavour === "remaining" && !noTarget && read.remaining < 0 ? RED : noTarget ? "text.disabled" : "text.primary" }}>
          {big}
        </Typography>
        <Typography component="div" sx={{ fontSize: 9.5, lineHeight: 1.3, color: markerText ? RED : "text.secondary", fontWeight: markerText ? 600 : 400, whiteSpace: "nowrap" }}>
          {markerText ?? detail}
        </Typography>
      </Box>
    </Tooltip>
  );
}

/** Per-clinician hover, alphabetical: a week against their own target, never a ranking. */
function CellTip({ layers, breakdown, state, group, territories, weekStart, tz }: { layers: string; breakdown: string; state: DemoState; group: DisciplineGroup; territories: string[]; weekStart: string; tz: string }) {
  const clinicians = selectCellClinicians(state, group, territories, weekStart);
  const heading = `Week of ${formatInBranchZone(dateKeyStart(weekStartKey(weekStart), tz), tz, "MMM d")}`;
  const lines = clinicians.map((c) => `${c.displayName} · ${c.firm} points · ${c.provisional} reserved · target ${c.target}`);
  return <span style={{ whiteSpace: "pre-line" }}>{[layers, breakdown, ...(lines.length ? [heading, ...lines] : [])].join("\n")}</span>;
}
